import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import API from "../utils/axiosInstance";
import { useTranslation } from "react-i18next";
import { 
  Plus, Edit2, Trash2, Eye, Save, X, Upload, 
  Users, TrendingUp, MessageSquare, Heart, FileText,
  Calendar, BarChart2, Clock
} from "lucide-react";
import "./AdminSubscriptionManagement.css";

const AdminSubscriptionManagement = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState("posts"); // posts, subscriptions, analytics
  const navigate = useNavigate();
 const { t, i18n } = useTranslation();
  // Post Management State
  const [subscriptions, setSubscriptions] = useState([]);
  const [posts, setPosts] = useState([]);
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [previewPost, setPreviewPost] = useState(null);
  const [postForm, setPostForm] = useState({
    subscriptionId: "",
    title: "",
    content: "",
    type: "text",
    attachmentUrl: "",
  });
  const [uploadingFile, setUploadingFile] = useState(false);

  // Subscription Dashboard State
  const [subscriptionUsers, setSubscriptionUsers] = useState({});
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  // Analytics State
  const [analytics, setAnalytics] = useState({
    totalSubscriptions: 0,
    totalPosts: 0,
    totalLikes: 0,
    totalComments: 0,
    activeUsers: 0,
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const isAdmin = user?.isAdmin === true || user?.isAdmin === 1 || user?.isAdmin === "1";
    
    if (user && isAdmin) {
      setIsAuthorized(true);
      fetchSubscriptions();
      fetchAllPosts();
      fetchAnalytics();
      fetchSubscriberStats();
    } else {
      alert("Access Denied: Only Admin can access this page.");
      navigate("/dashboard");
    }
  }, [navigate]);

  const fetchSubscriptions = async () => {
    try {
      const res = await API.get("/subscriptions/all");
      setSubscriptions(res.data || []);
      if (res.data?.[0]) {
        setPostForm({ ...postForm, subscriptionId: res.data[0].id });
      }
    } catch (err) {
      console.error("Failed to fetch subscriptions", err);
    }
  };

  const fetchAllPosts = async () => {
    try {
      const res = await API.get("/subscriptions/all");
      const allSubs = res.data || [];
      
      let allPosts = [];
      for (const sub of allSubs) {
        try {
          const postsRes = await API.get(`/subscriptions/posts/${sub.id}`);
          const subPosts = postsRes.data.map(post => ({
            ...post,
            subscriptionTitle: sub.title
          }));
          allPosts = [...allPosts, ...subPosts];
        } catch (err) {
          console.error(`Failed to fetch posts for subscription ${sub.id}`, err);
        }
      }
      setPosts(allPosts);
    } catch (err) {
      console.error("Failed to fetch posts", err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      // Mock analytics data - replace with actual API calls
      setAnalytics({
        totalSubscriptions: subscriptions.length,
        totalPosts: posts.length,
        totalLikes: posts.reduce((acc, post) => acc + (post.likeCount || 0), 0),
        totalComments: posts.reduce((acc, post) => acc + (post.commentCount || 0), 0),
        activeUsers: 156, // Mock data
      });
    } catch (err) {
      console.error("Failed to fetch analytics", err);
    }
  };

  const fetchSubscriberStats = async () => {
  try {
    const res = await API.get("/subscriptions/stats/subscribers");

    const map = {};
    res.data.forEach(item => {
      map[item.subscriptionId] = Number(item.count);
    });

    setSubscriptionUsers(map);
  } catch(err){
    console.error(err);
  }
};

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    
    if (file.type.startsWith('video/')) {
      formData.append('video', file);
      setPostForm({ ...postForm, type: 'video' });
    } else {
      formData.append('file', file);
    }

    try {
      const res = await API.post('/subscriptions/posts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPostForm({ ...postForm, attachmentUrl: res.data.url });
      alert("File uploaded successfully!");
    } catch (err) {
      console.error("Upload error:", err);
      alert("Failed to upload file");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCreatePost = async () => {
    if (!postForm.subscriptionId || !postForm.title || !postForm.content) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      if (editingPost) {
        // Update post - API endpoint may need to be added
        await API.put(`/subscriptions/posts/${editingPost.id}`, postForm);
        alert("Post updated successfully!");
      } else {
        await API.post("/subscriptions/posts", postForm);
        alert("Post created successfully!");
      }
      
      resetPostForm();
      fetchAllPosts();
    } catch (err) {
      console.error("Post creation error:", err);
      alert("Failed to create/update post");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      // Delete post - API endpoint may need to be added
      await API.delete(`/subscriptions/posts/${postId}`);
      alert("Post deleted successfully!");
      fetchAllPosts();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete post");
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      subscriptionId: post.subscriptionId,
      title: post.title,
      content: post.content,
      type: post.type,
      attachmentUrl: post.attachmentUrl || "",
    });
    setShowPostForm(true);
  };

  const resetPostForm = () => {
    setPostForm({
      subscriptionId: subscriptions[0]?.id || "",
      title: "",
      content: "",
      type: "text",
      attachmentUrl: "",
    });
    setEditingPost(null);
    setShowPostForm(false);
    setPreviewPost(null);
  };

  
  const handlePreviewPost = () => {
    setPreviewPost({
      ...postForm,
      createdAt: new Date().toISOString(),
      subscriptionTitle: subscriptions.find(s => s.id == postForm.subscriptionId)?.title
    });
  };

  if (!isAuthorized) return null;
const activeUsersCount = Object.values(subscriptionUsers)
  .reduce((acc, val) => acc + val, 0);

  return (
    <div className="dashboard-wrapper"  dir={["ar","fa","ir","he"].includes(i18n.language)?"rtl":"ltr"}
      style={{
        flexDirection: ["ar","fa","ir","he"].includes(i18n.language) ? "row-reverse" : "row",
      }}>
      <div className="sidebar-fixed">
        <Sidebar isOpen={isSidebarOpen} />
      </div>
      <div className="dashboard-main">
        <Navbar onToggleSidebar={() => setIsSidebarOpen((p) => !p)} />

        <div className="admin-subscription-container">
          <div className="admin-header">
            <h2 className="admin-title">Subscription Forum</h2>
            <p className="admin-subtitle">Manage posts, subscriptions, and view analytics</p>
          </div>

          {/* Tabs */}
          <div className="admin-tabs">
            <button 
              className={`tab-btn ${activeTab === "posts" ? "active" : ""}`}
              onClick={() => setActiveTab("posts")}
            >
              <FileText size={18} />
             {t("Post Management")}
            </button>
            <button 
              className={`tab-btn ${activeTab === "subscriptions" ? "active" : ""}`}
              onClick={() => setActiveTab("subscriptions")}
            >
              <Users size={18} />
              {t("Subscriptions")}
            </button>
            <button 
              className={`tab-btn ${activeTab === "analytics" ? "active" : ""}`}
              onClick={() => setActiveTab("analytics")}
            >
              <BarChart2 size={18} />
              {t("Analytics")}
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* POST MANAGEMENT TAB */}
            {activeTab === "posts" && (
              <div className="posts-management">
                <div className="section-header">
                  <h3>{t("Manage Posts")}</h3>
                  <button 
                    className="btn-primary"
                    onClick={() => setShowPostForm(!showPostForm)}
                  >
                    <Plus size={18} />
                    {showPostForm ? t("Cancel") : t("Create New Post")}
                  </button>
                </div>

                {/* Post Creation Form */}
                {showPostForm && (
                  <div className="post-form-card">
                    <h4>{editingPost ? t("Edit Post") : t("Create New Post")}</h4>
                    
                    <div className="form-group">
                      <label>{t("Subscription")}</label>
                      <select
                        className="form-control"
                        value={postForm.subscriptionId}
                        onChange={(e) => setPostForm({ ...postForm, subscriptionId: e.target.value })}
                      >
                        {subscriptions.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t("Title")} *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={postForm.title}
                        onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                        placeholder="Enter post title"
                      />
                    </div>

                    <div className="form-group">
                      <label>{t("Content")} *</label>
                      <textarea
                        className="form-control rich-editor"
                        value={postForm.content}
                        onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                        placeholder="Enter post content"
                        rows="8"
                      />
                      {/* <small className="form-text">
                        You can use HTML tags like &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;li&gt;, etc.
                      </small> */}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>{t("Post Type")}</label>
                        <select
                          className="form-control"
                          value={postForm.type}
                          onChange={(e) => setPostForm({ ...postForm, type: e.target.value })}
                        >
                          <option value="text">{t("Text")}</option>
                          <option value="url">URL</option>
                          <option value="file">{t("File/Document")}</option>
                          <option value="video">{t("Video")}</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>{t("Attachment")}</label>
                        <div className="file-upload-wrapper">
                          <input
                            type="file"
                            className="form-control"
                            onChange={handleFileUpload}
                            accept={postForm.type === 'video' ? 'video/*' : '*'}
                            disabled={uploadingFile}
                          />
                          <button 
                            className="btn-upload"
                            onClick={() => document.querySelector('.file-upload-wrapper input').click()}
                            disabled={uploadingFile}
                          >
                            <Upload size={16} />
                            {uploadingFile ? "Uploading..." : "Upload File"}
                          </button>
                        </div>
                        {postForm.attachmentUrl && (
                          <small className="form-text success">
                            ✓ {t("File uploaded successfully")}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Attachment URL (optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={postForm.attachmentUrl}
                        onChange={(e) => setPostForm({ ...postForm, attachmentUrl: e.target.value })}
                        placeholder="Or paste URL directly"
                      />
                    </div>

                    <div className="form-actions">
                      <button className="btn-secondary" onClick={handlePreviewPost}>
                        <Eye size={18} />
                        {t("Preview")}
                      </button>
                      <button className="btn-success" onClick={handleCreatePost}>
                        <Save size={18} />
                        {editingPost ? t("Update Post") : t("Publish Post")}
                      </button>
                      <button className="btn-outline" onClick={resetPostForm}>
                        <X size={18} />
                        {t("Cancel")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Preview Modal */}
                {previewPost && (
                  <div className="preview-modal">
                    <div className="preview-content">
                      <div className="preview-header">
                        <h4>Post Preview</h4>
                        <button onClick={() => setPreviewPost(null)} className="close-btn">
                          <X size={20} />
                        </button>
                      </div>
                      <div className="preview-body">
                        <div className="post-preview-card">
                          <div className="post-meta">
                            <span className="subscription-badge">{previewPost.subscriptionTitle}</span>
                            <span className="post-date">{new Date(previewPost.createdAt).toLocaleString()}</span>
                          </div>
                          <h3 className="preview-title">{previewPost.title}</h3>
                          <div className="preview-content-body" dangerouslySetInnerHTML={{ __html: previewPost.content }} />
                          {previewPost.attachmentUrl && (
                            <div className="preview-attachment">
                              {previewPost.type === "video" ? (
                                <video controls style={{ width: "100%", maxHeight: "400px" }}>
                                  <source src={previewPost.attachmentUrl} />
                                </video>
                              ) : (
                                <a href={previewPost.attachmentUrl} target="_blank" rel="noreferrer">
                                  📎 {previewPost.type === "file" ? "Download Attachment" : "View Link"}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="preview-footer">
                        <button className="btn-primary" onClick={() => setPreviewPost(null)}>
                          Close Preview
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Posts List */}
                <div className="posts-list">
                  <h4 className="list-title">{t("All Posts")} ({posts.length})</h4>
                  {posts.length === 0 ? (
                    <div className="empty-state">
                      <FileText size={48} />
                      <p>{t("No posts created yet")}</p>
                    </div>
                  ) : (
                    <div className="posts-grid">
                      {posts.map((post) => (
                        <div key={post.id} className="post-item">
                          <div className="post-item-header">
                            <div>
                              <span className="subscription-badge">{post.subscriptionTitle}</span>
                              <h5 className="post-item-title">{post.title}</h5>
                              <p className="post-item-date">
                                <Clock size={14} />
                                {new Date(post.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="post-item-stats">
                            <span><Heart size={14} /> {post.likeCount || 0} likes</span>
                            <span><MessageSquare size={14} /> {post.commentCount || 0} comments</span>
                          </div>
                          <div className="post-item-actions">
                            <button 
                              className="btn-icon btn-edit"
                              onClick={() => handleEditPost(post)}
                              title="Edit"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button 
                              className="btn-icon btn-delete"
                              onClick={() => handleDeletePost(post.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* SUBSCRIPTIONS TAB */}
            {activeTab === "subscriptions" && (
              <div className="subscriptions-dashboard">
                <div className="section-header">
                  <h3>{t("Subscription Overview")}</h3>
                </div>

                <div className="subscriptions-grid">
                  {subscriptions.map((sub) => (
                    <div key={sub.id} className="subscription-card">
                      <div className="subscription-header">
                        <h4>{sub.title}</h4>
                        <span className="subscription-id">ID: {sub.wooProductId}</span>
                      </div>
                      <p className="subscription-desc">{sub.description}</p>
                      <div className="subscription-stats">
                        <div className="stat-item">
                          <Users size={18} />
                          <div>
                            <span className="stat-value">
                              {subscriptionUsers[sub.id] || 0}
                            </span>
                            <span className="stat-label">{t("Subscribers")}</span>
                          </div>
                        </div>
                        <div className="stat-item">
                          <FileText size={18} />
                          <div>
                            <span className="stat-value">
                              {posts.filter(p => p.subscriptionId === sub.id).length}
                            </span>
                            <span className="stat-label">{t("Posts")}</span>
                          </div>
                        </div>
                      </div>
                      {/* <button 
                        className="btn-outline-small"
                        onClick={() => setSelectedSubscription(sub)}
                      >
                        View Details
                      </button> */}
                    </div>
                  ))}
                </div>

                {subscriptions.length === 0 && (
                  <div className="empty-state">
                    <Users size={48} />
                    <p>{t("No subscriptions available")}</p>
                  </div>
                )}
              </div>
            )}

            {/* ANALYTICS TAB */}
            {activeTab === "analytics" && (
              <div className="analytics-dashboard">
                <div className="section-header">
                  <h3>{t("Analytics & Reports")}</h3>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-icon blue">
                      <FileText size={24} />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">{posts.length}</span>
                      <span className="stat-label">{t("Total Posts")}</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon green">
                      <Users size={24} />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">{subscriptions.length}</span>
                      <span className="stat-label">{t("Subscriptions")}</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon pink">
                      <Heart size={24} />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {posts.reduce((acc, post) => acc + (post.likeCount || 0), 0)}
                      </span>
                      <span className="stat-label">{t("Total Likes")}</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon purple">
                      <MessageSquare size={24} />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {posts.reduce((acc, post) => acc + (post.commentCount || 0), 0)}
                      </span>
                      <span className="stat-label">{t("Total Comments")}</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon orange">
                      <TrendingUp size={24} />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">{activeUsersCount}</span>
                      <span className="stat-label">{t("Active Users")}</span>
                    </div>
                  </div>

                  <div className="stat-card">
                    <div className="stat-icon teal">
                      <Calendar size={24} />
                    </div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {posts.filter(p => {
                          const postDate = new Date(p.createdAt);
                          const now = new Date();
                          const diffTime = Math.abs(now - postDate);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return diffDays <= 7;
                        }).length}
                      </span>
                      <span className="stat-label">{t("Posts This Week")}</span>
                    </div>
                  </div>
                </div>

                {/* Engagement Overview */}
                <div className="engagement-section">
                  <h4>{t("Post Engagement")}</h4>
                  <div className="engagement-table">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{t("Post Title")}</th>
                          <th>{t("Subscription")}</th>
                          <th>{t("Likes")}</th>
                          <th>{t("Comments")}</th>
                          <th>{t("Date")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {posts.slice(0, 10).map((post) => (
                          <tr key={post.id}>
                            <td>{post.title}</td>
                            <td><span className="badge">{post.subscriptionTitle}</span></td>
                            <td>{post.likeCount || 0}</td>
                            <td>{post.commentCount || 0}</td>
                            <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="activity-section">
                  <h4>{t("Recent Activity")}</h4>
                  <div className="activity-list">
                    {posts.slice(0, 5).map((post, idx) => (
                      <div key={idx} className="activity-item">
                        <div className="activity-icon">
                          <FileText size={16} />
                        </div>
                        <div className="activity-details">
                          <p><strong>{t("New post")}:</strong> {post.title}</p>
                          <span className="activity-time">
                            {new Date(post.createdAt).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptionManagement;
